import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";

import { API_BASE_URL, ApiError, staffToken } from "@/api/client";

/**
 * STAFF SIGN-IN.
 *
 * It posts to the SAME /v1/auth/login the customer app uses — there is one identity system,
 * and staff are users with a flag (`users.is_platform_staff`), not a parallel account
 * universe. What differs is what the token can then REACH: the /v1/admin/* routes check the
 * flag, and an organization owner who signs in here gets a 403 on every one of them.
 *
 * So this screen cannot, and does not try to, decide who is staff. It signs a human in and
 * lets the server refuse them. A front end that gated on a flag it read out of a response
 * body would be an authorization check in a place the user can edit.
 */
export function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setPending(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/v1/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const problem = (await response.json().catch(() => null)) as { detail?: string } | null;
        throw new ApiError(response.status, "Sign-in failed", problem?.detail ?? "Check your credentials.");
      }

      const session = (await response.json()) as { access_token: string };
      staffToken.set(session.access_token);

      // Whether this human is actually staff is the SERVER's decision, and it is about to
      // make it: the organizations list is behind the staff gate, so a non-staff user who
      // signs in here lands on a 403 rather than a console.
      await navigate({ to: "/organizations" });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Sign-in failed.");
    } finally {
      setPending(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6">
      <h1 className="flex items-center gap-2 text-2xl font-semibold">
        Tankly
        <span className="rounded bg-[var(--color-alarm)] px-1.5 py-0.5 text-xs font-bold uppercase tracking-wide text-[var(--color-alarm-ink)]">
          Staff
        </span>
      </h1>
      <p className="mt-2 text-sm text-[var(--color-muted)]">
        The platform back-office. Everything you do here is written to the audit log.
      </p>

      <form onSubmit={submit} className="panel mt-8 space-y-4 p-6">
        <label className="block text-sm">
          <span className="text-[var(--color-muted)]">Email</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full rounded border border-[var(--color-rule-strong)] bg-[var(--color-ink)] px-3 py-2 text-[var(--color-text)]"
          />
        </label>

        <label className="block text-sm">
          <span className="text-[var(--color-muted)]">Password</span>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1 w-full rounded border border-[var(--color-rule-strong)] bg-[var(--color-ink)] px-3 py-2 text-[var(--color-text)]"
          />
        </label>

        {error && (
          <p role="alert" className="text-sm text-[var(--color-dry)]">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={pending}
          className="w-full rounded bg-[var(--color-cream)] px-4 py-2 font-semibold text-[var(--color-ink)] hover:opacity-90 disabled:opacity-50"
        >
          {pending ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </main>
  );
}
